import { useEffect, useMemo, useRef, useState } from "react";
import Icon from "./Icon";
import { Avatar } from "./ui";
import FloatingPopover from "./FloatingPopover";

// Combobox de personas pensado para organizaciones grandes: NO lista a todo el
// mundo — muestra solo los seleccionados (chips) y un campo de búsqueda grande
// y evidente; al enfocar/escribir despliega hasta 8 coincidencias.
//
//   <PersonPicker users={users} value={id} onChange={(id) => ...} />            (uno)
//   <PersonPicker users={users} multi values={[ids]} onChange={(ids) => ...} /> (varios)

const nq = (s) => (s || "").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");

export default function PersonPicker({
  users = [], value = "", values = [], onChange,
  multi = false, placeholder = "Escribe un nombre para buscar…",
  accent = "brand", exclude = [],
}) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(0);        // índice resaltado (teclado)
  const boxRef = useRef(null);
  const menuRef = useRef(null);

  const selIds = multi ? (values || []) : (value ? [value] : []);
  const selected = selIds.map((id) => users.find((u) => u.id === id)).filter(Boolean);

  const matches = useMemo(() => {
    const taken = new Set([...selIds, ...exclude]);
    const pool = users.filter((u) => !taken.has(u.id));
    const query = nq(q);
    const list = query
      ? pool.filter((u) => nq(u.full_name).includes(query) || nq(u.email).includes(query))
      : pool;
    return [...list].sort((a, b) => a.full_name.localeCompare(b.full_name)).slice(0, 8);
  }, [users, q, selIds, exclude]);

  useEffect(() => { setHi(0); }, [q, open]);

  // Cerrar al hacer clic fuera.
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => { if (!boxRef.current?.contains(e.target) && !menuRef.current?.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const pick = (u) => {
    if (multi) onChange([...(values || []), u.id]);
    else { onChange(u.id); setOpen(false); }
    setQ("");
  };
  const remove = (id) => {
    if (multi) onChange((values || []).filter((x) => x !== id));
    else onChange("");
  };

  const onKey = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setOpen(true); setHi((h) => Math.min(h + 1, matches.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHi((h) => Math.max(h - 1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); if (open && matches[hi]) pick(matches[hi]); }
    else if (e.key === "Escape") setOpen(false);
  };

  // Un color por tipo de participante: de un vistazo se distingue quién manda
  // (responsable), quién acompaña (colaborador) y quién supervisa (PMO).
  const ACENTOS = {
    violet: { chip: "border-violet-200 bg-violet-50 text-violet-700", x: "text-violet-400 hover:text-violet-700", ring: "focus-within:border-violet-400 focus-within:ring-violet-100" },
    emerald: { chip: "border-emerald-200 bg-emerald-50 text-emerald-700", x: "text-emerald-400 hover:text-emerald-700", ring: "focus-within:border-emerald-400 focus-within:ring-emerald-100" },
    brand: { chip: "border-brand-200 bg-brand-50 text-brand-700", x: "text-brand-400 hover:text-brand-700", ring: "focus-within:border-brand-400 focus-within:ring-brand-100" },
  };
  const ac = ACENTOS[accent] || ACENTOS.brand;

  return (
    <div ref={boxRef} className="relative">
      {/* Caja: chips seleccionados + input de búsqueda SIEMPRE visible */}
      <div className={`flex min-h-[42px] flex-wrap items-center gap-1.5 rounded-lg border-2 border-slate-200 bg-white px-2 py-1.5 transition-colors focus-within:ring-2 ${ac.ring}`}
        onClick={() => boxRef.current?.querySelector("input")?.focus()}>
        {selected.map((u) => (
          <span key={u.id} className={`inline-flex items-center gap-1.5 rounded-tag border py-0.5 pl-1 pr-1.5 text-[11px] font-medium ${ac.chip}`}>
            <Avatar name={u.full_name} size={18} />
            <span className="max-w-[140px] truncate">{u.full_name}</span>
            <button type="button" className={ac.x} title="Quitar" onClick={(e) => { e.stopPropagation(); remove(u.id); }}>
              <Icon name="x" size={12} />
            </button>
          </span>
        ))}
        <div className="relative flex min-w-[180px] flex-1 items-center gap-1.5">
          <Icon name="search" size={14} className="shrink-0 text-slate-400" />
          <input
            className="w-full border-0 bg-transparent p-0.5 text-xs text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-0"
            value={q} placeholder={selected.length && !multi ? "Cambiar…" : placeholder}
            onChange={(e) => { setQ(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)} onKeyDown={onKey}
          />
        </div>
      </div>

      {/* Resultados */}
      {open && (
        <FloatingPopover anchorRef={boxRef} maxHeight={256} className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-pop">
          <div ref={menuRef}>
          {matches.length === 0 ? (
            <p className="px-3 py-2.5 text-xs text-slate-400">Sin coincidencias con "{q}"</p>
          ) : (
            <ul className="max-h-56 overflow-y-auto py-1">
              {matches.map((u, i) => (
                <li key={u.id}>
                  <button type="button"
                    className={`flex w-full items-center gap-2.5 px-3 py-1.5 text-left ${i === hi ? "bg-slate-100" : "hover:bg-slate-50"}`}
                    onMouseEnter={() => setHi(i)} onClick={() => pick(u)}>
                    <Avatar name={u.full_name} size={26} />
                    <span className="min-w-0">
                      <span className="block truncate text-xs font-semibold text-slate-700">{u.full_name}</span>
                      <span className="block truncate text-[11px] text-slate-400">{u.email}</span>
                    </span>
                    <Icon name="plus" size={13} className="ml-auto shrink-0 text-slate-300" />
                  </button>
                </li>
              ))}
            </ul>
          )}
          {!q && users.length > 8 && (
            <p className="border-t border-slate-100 bg-slate-50 px-3 py-1 text-[11px] text-slate-400">
              Mostrando 8 de {users.length} — escribe para buscar
            </p>
          )}
          </div>
        </FloatingPopover>
      )}
    </div>
  );
}
